import { routing } from "@/i18n/routing";

/**
 * Prefix an internal href with the active locale, honouring
 * `localePrefix: "as-needed"` — the default locale stays unprefixed, others get
 * `/<locale>`. Mirrors `localizedUrl()` in `seo/alternates.ts` (which builds
 * absolute URLs for hreflang/sitemap); this is the relative, in-app twin.
 *
 * Why this exists instead of next-intl's `Link`: `createNavigation`'s BaseLink
 * calls `usePathname()` unconditionally (it needs it only to sync the locale
 * cookie in onClick — a feature we never use, since we never pass an explicit
 * `locale` prop). `usePathname()` is unknowable at prerender on a `[slug]`
 * route, so a next-intl Link anywhere in the Nav/Footer shell makes the static
 * shell dynamic and fails the build with "Uncached data accessed outside of
 * <Suspense>". Verified — it breaks `/[locale]/professionals/[slug]`.
 * `useLocale()` is context-based and static-safe, so this stays prerenderable.
 */
export function localeHref(href: string, locale: string): string {
  if (locale === routing.defaultLocale) return href;
  // Only in-app absolute paths get prefixed. An external URL, a bare hash, or a
  // query-only href must pass through untouched.
  if (!href.startsWith("/")) return href;
  // "/" must become "/vi", never "/vi/" — a trailing slash is a distinct URL to
  // a crawler and would redirect.
  return href === "/" ? `/${locale}` : `/${locale}${href}`;
}

/**
 * A router whose `push`/`replace` take UNPREFIXED in-app hrefs and land on the
 * active locale's URL, the way `LocaleLink` does for `<a>`. Pure so it can be
 * tested without React; `useLocaleRouter()` is the hook that feeds it.
 *
 * Why: every search surface pushed bare `/search?…` through `next/navigation`,
 * and with `localeDetection: false` nothing re-adds the prefix, so a VI reader
 * following a suggestion or changing a filter was dropped onto the EN page.
 */
export interface LocaleAwareRouter<R extends { push: (href: string, ...rest: never[]) => void; replace: (href: string, ...rest: never[]) => void }> {
  push: R["push"];
  replace: R["replace"];
}

export function localizeRouter<
  R extends { push: (href: string, ...rest: never[]) => void; replace: (href: string, ...rest: never[]) => void },
>(router: R, locale: string): Omit<R, "push" | "replace"> & LocaleAwareRouter<R> {
  return {
    ...router,
    push: ((href: string, ...rest: never[]) => router.push(localeHref(href, locale), ...rest)) as R["push"],
    replace: ((href: string, ...rest: never[]) => router.replace(localeHref(href, locale), ...rest)) as R["replace"],
  };
}
